import { yupResolver } from "@hookform/resolvers/yup";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { Controller, FormProvider, useForm } from "react-hook-form";
import yup from "../../../../utils/yup";

import type { FC } from "react";
import type { FieldError } from "react-hook-form";

const dividerChoiceSchema = yup.object({
  type: yup
    .string()
    .oneOf(["primary", "secondary"])
    .default(undefined)
    .required("Select a divider"),
});

type DividerChoiceForm = yup.InferType<typeof dividerChoiceSchema>;

export const AddDividerForm: FC<{
  onCancel: () => void;
  onConfirm: (data: DividerChoiceForm) => void;
  open: boolean;
  initialState?: { type?: "primary" | "secondary" };
}> = ({ onCancel, onConfirm, open, initialState }) => {
  const methods = useForm<DividerChoiceForm>({
    defaultValues: initialState,
    resolver: yupResolver(dividerChoiceSchema),
  });

  const { control, handleSubmit } = methods;

  return (
    <Dialog onClose={onCancel} open={open}>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onConfirm)}>
          <DialogTitle>Add Divider</DialogTitle>
          <DialogContent sx={{ minWidth: "500px" }}>
            <Controller
              name="type"
              control={control}
              render={({ field, fieldState: { error } }) => (
                <DividerSelect onChange={field.onChange} selected={field.value} error={error} />
              )}
            />
          </DialogContent>
          <DialogActions>
            <Button
              data-analytics-id="lexical-divider-dialog-cancel"
              type="button"
              onClick={onCancel}
              color="error"
            >
              Cancel
            </Button>
            <Button
              data-analytics-id="lexical-divider-dialog-confirm"
              type="submit"
              color="success"
            >
              Confirm
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  );
};

const DividerSelect: FC<{
  selected?: "primary" | "secondary";
  onChange: (selected: "primary" | "secondary") => void;
  error?: FieldError;
}> = ({ selected, onChange, error }) => (
  <Stack direction="column" spacing={1}>
    <BrandComponentDivider
      type="primary"
      onClick={() => onChange("primary")}
      selected={selected === "primary"}
    />
    <BrandComponentDivider
      type="secondary"
      onClick={() => onChange("secondary")}
      selected={selected === "secondary"}
    />
    {error && (
      <Typography color="error" variant="caption">
        {error.message}
      </Typography>
    )}
  </Stack>
);

const BrandComponentDivider: FC<{
  type: "primary" | "secondary";
  selected: boolean;
  onClick?: () => void;
}> = ({ type, selected, onClick }) => (
  <Button
    data-analytics-id={`lexical-divider-dialog-${type}`}
    onClick={onClick}
    variant="text"
    fullWidth
    sx={
      selected
        ? {
            borderWidth: "3px",
            borderColor: "primary.main",
            borderStyle: "solid",
          }
        : {}
    }
  >
    <Box p={2} sx={{ width: "100%" }} className="divider_button_paper_colour">
      <hr className={type} />
    </Box>
  </Button>
);
